/* Offer card — the first-order offer, above the bottom rail (Sep 2026). One of the rail cards: placement,
   open/close, triggers and analytics come from assets/rail-card.js; markup, styling and copy are in
   sections/bottom-rail.liquid (<offer-card>, the .rail-card__* and .offer-card__* rules, the "Offer card"
   settings).

   It replaces the Klaviyo "Email Popup" form (VBa2TW): that form interrupted every first visit at five seconds
   with a scroll-locked modal, and in its last 90 days six in ten viewers closed it and under one in a hundred
   signed up. The offer itself (10OFF4U) is the best-converting discount the store has, so the offer stays and
   only the delivery changes.

   What this file adds to the base:
   - It opens by itself once, on the SECOND page view of a session (sessionStorage), never on a first — and never
     for a signed-in customer, a visitor who has signed up here, a visitor who dismissed it in the last 30 days,
     a visitor arriving from a Klaviyo email (_kx in the URL: already on the list), on the cart page, in the
     theme editor, or while Ami, a header card or the explainer is open. The rail message that carries the
     offer is on screen from the first page, so it is discoverable before then and after a dismissal.
   - The email goes to Klaviyo's public client subscriptions endpoint with the company id — the same call the
     Klaviyo form made, with its own custom_source — and lands on the Newsletter list, which is what triggers the
     "Email Welcome Series with Discount" flow. No backend, no proxy route. The band then says the after-sign-up
     lines and the body is the code with a copy control; the code is static and never auto-applied (Anthony:
     copy only).

   PREVIEW_ALWAYS_OPEN: set it to true to have the card open on every page load regardless of the rules above,
   for looking at it in theme dev. It must be false on main. */

(() => {
  'use strict';

  if (!window.RailCard) return; // rail-card.js has not landed: no card rather than a broken one

  const PREVIEW_ALWAYS_OPEN = false; // true only while looking at the card in theme dev; never on main

  const { RailCard, COMPACT, somethingElseIsOpen, track } = window.RailCard;

  const KLAVIYO_CLIENT_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  const KLAVIYO_REVISION = '2025-07-15';

  const VIEWS_KEY = 's2OfferViews'; // sessionStorage: page views this session
  const AUTO_KEY = 's2OfferAutoOpened'; // sessionStorage: already opened itself this session
  const FROM_EMAIL_KEY = 's2OfferFromEmail'; // sessionStorage: arrived from a Klaviyo email this session
  const DISMISSED_KEY = 's2OfferDismissedAt'; // localStorage: epoch ms of the last dismissal
  const SIGNED_KEY = 's2OfferSignedUp'; // localStorage: signed up through this card
  const DISMISS_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;
  const AUTO_OPEN_ON_VIEW = 2;
  const AUTO_OPEN_DELAY_MS = 1200; // after load, so the page has settled under it
  const COPIED_HOLD_MS = 2000;

  function storage(kind) {
    try {
      return kind === 'session' ? window.sessionStorage : window.localStorage;
    } catch {
      return null; // private mode, blocked storage: the card simply never opens itself
    }
  }

  function readKey(kind, key) {
    const store = storage(kind);
    if (!store) return null;
    try {
      return store.getItem(key);
    } catch {
      return null;
    }
  }

  function writeKey(kind, key, value) {
    const store = storage(kind);
    if (!store) return;
    try {
      store.setItem(key, value);
    } catch {
      /* quota or blocked: not worth surfacing */
    }
  }

  function isValidEmail(value) {
    // Enough to catch typos before the round trip; Klaviyo validates for real.
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  }

  class OfferCard extends RailCard {
    triggerActions() {
      return ['open-offer-card'];
    }

    eventName() {
      return 'Offer Card';
    }

    openProps() {
      return { view: this.doneEl && !this.doneEl.hidden ? 'code' : 'ask' };
    }

    onWire() {
      this.formEl = this.querySelector('[data-offer-form]');
      this.inputEl = this.querySelector('[data-offer-input]');
      this.submitEl = this.querySelector('[data-offer-submit]');
      this.errorEl = this.querySelector('[data-offer-error]');
      this.askEl = this.querySelector('[data-offer-view="ask"]');
      this.doneEl = this.querySelector('[data-offer-view="done"]');
      this.codeEl = this.querySelector('[data-offer-code]');
      this.codeActionEl = this.querySelector('[data-offer-code-action]');
      this.honeypotEl = this.querySelector('[data-offer-hp]');
      this.bandTitleEl = this.querySelector('[data-offer-band-title]');
      this.bandTextEl = this.querySelector('[data-offer-band-text]');

      this.companyId = this.getAttribute('data-offer-company') || '';
      this.listId = this.getAttribute('data-offer-list') || '';
      this.code = this.getAttribute('data-offer-code') || '';
      this.source = this.getAttribute('data-offer-source') || 'Rail offer card';
      this.isCustomer = this.hasAttribute('data-offer-customer');
      this.autoAllowed = !this.hasAttribute('data-offer-no-auto'); // cart page etc., decided in Liquid

      this.copyLabel = this.codeActionEl ? this.codeActionEl.textContent : 'Copy';
      this.copiedLabel = this.getAttribute('data-offer-copied-label') || 'Copied';

      if (this.formEl) this.formEl.addEventListener('submit', (event) => this.onSubmit(event));
      if (this.inputEl) this.inputEl.addEventListener('input', () => this.setError(''));
      if (this.codeEl) this.codeEl.addEventListener('click', () => this.copyCode());

      this.considerAutoOpen();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      window.clearTimeout(this.autoTimer);
      window.clearTimeout(this.copiedTimer);
    }

    onOpen(by) {
      this.setError('');
      /* Focus lands in the field only when the visitor asked for the card; an automatic open never steals it. */
      if (this.inputEl && this.askEl && !this.askEl.hidden && by === 'rail' && !COMPACT.matches) {
        this.inputEl.focus({ preventScroll: true });
      }
    }

    /* Only a deliberate dismissal (the close control, Escape, the outside click, the trigger) starts the 30-day
       quiet period; being displaced by another card is not the visitor's decision. Signing up ends the
       auto-opens for good, so nothing to write there. */
    onClose(by) {
      if (by === 'close' || by === 'escape' || by === 'outside' || by === 'trigger') {
        if (!readKey('local', SIGNED_KEY)) writeKey('local', DISMISSED_KEY, String(Date.now()));
      }
    }

    /* ---------- when it opens by itself ---------- */

    considerAutoOpen() {
      if (this.designMode) return;

      if (PREVIEW_ALWAYS_OPEN) {
        this.autoTimer = window.setTimeout(() => this.open('preview'), AUTO_OPEN_DELAY_MS);
        return;
      }

      if (!this.autoAllowed || this.isCustomer) return;
      if (readKey('local', SIGNED_KEY)) return;

      const dismissedAt = Number(readKey('local', DISMISSED_KEY) || 0);
      if (dismissedAt && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) return;

      /* Arrived from a Klaviyo email: already on the list, and the code is in their inbox. Remembered for the
         session so the next page (without the parameter) does not open it. */
      let fromKlaviyo = readKey('session', FROM_EMAIL_KEY) === '1';
      try {
        if (new URLSearchParams(window.location.search).has('_kx')) {
          fromKlaviyo = true;
          writeKey('session', FROM_EMAIL_KEY, '1');
        }
      } catch {
        /* malformed query: treat as not from email */
      }
      if (fromKlaviyo) return;

      const views = Number(readKey('session', VIEWS_KEY) || 0) + 1;
      writeKey('session', VIEWS_KEY, String(views));
      if (views < AUTO_OPEN_ON_VIEW) return;
      if (readKey('session', AUTO_KEY)) return;

      this.autoTimer = window.setTimeout(() => {
        if (somethingElseIsOpen()) return; // this page is busy; a later page gets its turn
        writeKey('session', AUTO_KEY, '1');
        this.open('auto');
      }, AUTO_OPEN_DELAY_MS);
    }

    /* ---------- sign-up ---------- */

    setError(text) {
      if (!this.errorEl) return;
      this.errorEl.textContent = text;
      this.errorEl.hidden = !text;
      if (this.inputEl) this.inputEl.setAttribute('aria-invalid', text ? 'true' : 'false');
    }

    setBusy(busy) {
      if (this.submitEl) this.submitEl.disabled = busy;
      if (this.inputEl) this.inputEl.readOnly = busy;
      this.toggleAttribute('data-busy', busy);
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.honeypotEl && this.honeypotEl.value) return; // a bot filled the field nobody can see
      const email = this.inputEl ? this.inputEl.value.trim() : '';
      if (!isValidEmail(email)) {
        this.setError(this.getAttribute('data-offer-invalid') || 'That address does not look right.');
        if (this.inputEl) this.inputEl.focus();
        return;
      }

      this.setBusy(true);
      this.setError('');
      const ok = this.designMode ? true : await this.subscribe(email);
      this.setBusy(false);

      if (!ok) {
        this.setError(this.getAttribute('data-offer-failed') || "That didn't go through. Check the address and try again.");
        return;
      }

      writeKey('local', SIGNED_KEY, '1');
      this.showCode();
      track('Offer Signed Up', { opened_by: this.openedBy, design_mode: this.designMode });
    }

    /* Klaviyo's client subscriptions endpoint: public, keyed by the company id, CORS-enabled — the same call the
       onsite form made. 202 with no body on success; a 4xx carries a JSON:API errors array. custom_source is
       what Klaviyo reports the sign-up under, so this card is separable from the footer and the old popup. */
    async subscribe(email) {
      if (!this.companyId || !this.listId) return false;
      const body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.source,
            profile: {
              data: {
                type: 'profile',
                attributes: { email },
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: this.listId } },
          },
        },
      };
      try {
        const response = await fetch(`${KLAVIYO_CLIENT_ENDPOINT}?company_id=${encodeURIComponent(this.companyId)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            revision: KLAVIYO_REVISION,
          },
          body: JSON.stringify(body),
          signal: typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(10000) : undefined,
        });
        return response.ok || response.status === 202;
      } catch {
        return false;
      }
    }

    /* The band says one thing at a time: its heading and text become the after-sign-up lines (the section's
       settings, carried on the element), and the body is then just the code. */
    showCode() {
      const doneTitle = this.getAttribute('data-offer-done-title');
      const doneText = this.getAttribute('data-offer-done-text');
      if (this.bandTitleEl && doneTitle) this.bandTitleEl.textContent = doneTitle;
      if (this.bandTextEl && doneText) this.bandTextEl.textContent = doneText;
      if (this.askEl) this.askEl.hidden = true;
      if (this.doneEl) this.doneEl.hidden = false;
      this.schedulePosition(); // the card's height changed; its bottom edge must not
      if (this.codeEl && !COMPACT.matches) this.codeEl.focus({ preventScroll: true });
    }

    async copyCode() {
      if (!this.code) return;
      let copied = false;
      try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          await navigator.clipboard.writeText(this.code);
          copied = true;
        }
      } catch {
        copied = false;
      }
      if (!copied) {
        /* No clipboard API (insecure context, old engine): select the code so a manual copy is one keystroke. */
        try {
          const range = document.createRange();
          range.selectNodeContents(this.codeEl.querySelector('[data-offer-code-text]') || this.codeEl);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
          copied = document.execCommand && document.execCommand('copy');
        } catch {
          copied = false;
        }
      }
      if (!copied) return;
      this.codeEl.setAttribute('data-copied', 'true');
      if (this.codeActionEl) this.codeActionEl.textContent = this.copiedLabel;
      window.clearTimeout(this.copiedTimer);
      this.copiedTimer = window.setTimeout(() => {
        this.codeEl.removeAttribute('data-copied');
        if (this.codeActionEl) this.codeActionEl.textContent = this.copyLabel;
      }, COPIED_HOLD_MS);
      track('Offer Code Copied', { opened_by: this.openedBy });
    }
  }

  if (!window.customElements.get('offer-card')) {
    window.customElements.define('offer-card', OfferCard);
  }

  /* For the rail, Ami or anything else that wants to open it by hand. */
  window.OfferCard = {
    open(by) {
      const el = document.querySelector('offer-card');
      if (el && typeof el.open === 'function') el.open(by || 'api');
    },
    close() {
      const el = document.querySelector('offer-card');
      if (el && typeof el.close === 'function') el.close('api');
    },
  };
})();
